import ModelEntity from "../../model-entity";
import DatetimeUtils from "../../../gd/utils/datetime-utils";
import ErrorProto from "../../../gd/validation/error";
import ErrorFieldRequired from "../../../gd/validation/error-field-required";
import ErrorFieldCustom from "../../../gd/validation/error-field-custom";
import {WebhookStatus} from "./@const";

/**
 * Webhook v2 — a destination and nothing else: url + secret.
 * WHEN something is sent is decided by the automation holding an
 * `action-send-webhook` action pointing here.
 */
export default class Webhook extends ModelEntity {

    public companyId        : ID
    public name             : string
    public endpointUrl      : string
    public status           : WebhookStatus
    public errorCounter     : number
    public createdByUserId? : ID
    public momentCreated?   : Moment

    // Owned by the client: generated here for a new webhook (see
    // generate-secret.ts) and editable afterwards. Sent on create and on
    // update; the list returns it, so the edit view always has it.
    public secret           : string

    constructor(data:any) {

        super(_.get(data, 'id', ''));

        this.companyId          = _.get(data, 'companyId', gd.companyId)
        this.name               = _.get(data, 'name', '') || ''
        this.endpointUrl        = _.get(data, 'endpointUrl', '') || ''
        this.status             = Number(_.get(data, 'status', WebhookStatus.ACTIVE))
        this.errorCounter       = Number(_.get(data, 'errorCounter', 0))
        this.createdByUserId    = _.get(data, 'createdByUserId', null)

        const momentCreated     = _.get(data, 'momentCreated', null)
        this.momentCreated      = momentCreated ? DatetimeUtils.datetimeToMoment(momentCreated) : undefined

        this.secret             = _.get(data, 'secret', '') || ''

        return this;
    }

    get isSuspended(): boolean {
        return this.status === WebhookStatus.SUSPENDED
    }

    get isActive(): boolean {
        return this.status === WebhookStatus.ACTIVE
    }

    // What the picker and the action descriptions show — an unnamed webhook
    // still has to be recognisable.
    get title(): string {
        return this.name || this.endpointUrl
    }

    clone(): Webhook {
        return new Webhook(this.toJSON())
    }

    validate(): ErrorProto[] | null {

        const errors : ErrorProto[] = []

        if (!this.endpointUrl) {
            errors.push(new ErrorFieldRequired(gd.fields.webhook.endpointUrl))
        } else if (!/^https?:\/\//i.test(this.endpointUrl)) {
            // Same rule the server enforces on create/update.
            errors.push(new ErrorFieldCustom(gd.fields.webhook.endpointUrl, {
                message: '{0} must start with http:// or https://'
            }))
        }

        return errors.length ? errors : null
    }

    toJSON(): JSONObject {
        return {
            id              : this.id,
            companyId       : this.companyId,
            name            : this.name,
            endpointUrl     : this.endpointUrl,
            secret          : this.secret,
            status          : this.status,
            errorCounter    : this.errorCounter,
            createdByUserId : this.createdByUserId,
            momentCreated   : this.momentCreated ? DatetimeUtils.momentToUTCISODatetime(this.momentCreated) : null,
        }
    }
}
